import { Button, capitalize, CircularProgress, Dialog, DialogActions, DialogContent, Divider, IconButton } from "@mui/material";
import { Component } from "react";
import DialogTitleWithCloseButton from "../../../../components/DialogTitleWithCloseButton";
import swal from 'sweetalert';
import request from '../../../../request';
import CenteredMessage from '../../../../components/CenteredMessage';
import Setting from "./Setting";
import RoundedIconButton from "../../../../components/RoundedIconButton";
import RefreshIcon from '@mui/icons-material/Refresh';
import { connect } from "react-redux";
import { USER_ROLE } from "../../../../../../shared/constants";
import { hasIntersection } from "../../../../../../shared/utils";
import PillLabel from "../../../../components/PillLabel";
import quickbooks from "../../../../quickbooks";
import QuickbooksEntity from "./QuickbooksEntity";
import AttachmentInput from "../../../../components/AttachmentInput";
import { Upload } from "@mui/icons-material";
import { errorToast } from "../../../../toast";


class _SystemSettings extends Component {

   state = {
      loading: true,
      settings: null,
		excelFile: null
   }

	importExcel = async () => {

		if (!this.state.excelFile) {
			errorToast('Please select a file to import');
			return;
		}

		try {

			this.setState({ loading: true });

			// upload file
			const base64 = this.state.excelFile.base64;
			await request.post('/api/admin/backdated-data', { base64 });

			swal("Success", "Data imported successfully", 'success');
			this.setState({ excelFile: null });

		} catch (err) {
			swal("Error", err.message, 'error');
		} finally {
			this.setState({ loading: false });
		}
	}

   fetchSettings = async () => {

      try {

         this.setState({ loading: true });

         const res = await request.get('/api/general/system-settings');
         this.setState({ settings: res.data });

      } catch (err) {
         swal("Error", err.message, 'error');
      } finally {
         this.setState({ loading: false });
      }
   }

	getQuickbooksCOnfigLink = async () => {

		try {

			this.setState({ loading: true });
			await quickbooks.connect();

			// NOTE: I have intenttionally ommited disabling the loading indicator
			// so that the page will show loading until the user is redirected to quickbooks
			// otherwise it will look like nothing is happening

		} catch (err) {
			swal("Error", err.message, 'error');
			this.setState({ loading: false });
		}

	}

   componentDidMount() {
      this.fetchSettings();
   }

   render() {

		const { settings, loading } = this.state;
		let content;

		const allowEditingSystemSettings = hasIntersection(this.props.roles, [ USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN ]);
		const allowEditingQuickbooksSettings = hasIntersection(this.props.roles, [ USER_ROLE.SUPER_ADMIN, USER_ROLE.FINANCE_MANAGER ]);
		const allowUploadingExcel = hasIntersection(this.props.roles, [ USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN ]);


		if (loading) {
			content = <div className="flex justify-center items-center h-[200px]">
				<CircularProgress />
			</div>
		} else if (settings === null) {
			content = <div className="flex justify-center items-center h-[200px]">
				<CenteredMessage
					message="Failed to load settings"
					onRefresh={this.fetchSettings}	
				/>
			</div>
		} else {


			content = <div className="grid grid-cols-1 sm:grid-cols-2 gap-16 p-4 text-gray-600">

				<div className="flex flex-col gap-2">
					<div className="grid grid-cols-[1fr,auto] gap-4">
						<div className="text-lg font-bold">Quickbooks</div>
						<div className="text-xs flex items-center">
							<PillLabel className={`uppercase text-white text-[9px] px-[10px] py-[1px] font-bold ${settings.quickbooks_connected ? 'bg-green-500' : 'bg-gray-500'}`}>
								{settings.quickbooks_connected ? 'Connected' : 'Not connected'}
							</PillLabel>
						</div>
					</div>
					<p className="text-xs">
						Connect your Quickbooks account to sync invoices and payments.
					</p>
					<Divider />
					{
						!settings.quickbooks_connected && <div>
							<Button variant="contained" size="small" className="rounded-full px-4" onClick={this.getQuickbooksCOnfigLink}>
								CONNECT
							</Button>
						</div>
					}
					{
						settings.quickbooks_connected && [ 
							{
								key: 'quickbooks_sales_income_account',
								title: 'Sales Income Account',
								description: 'The account where income from loan payments will be recorded.',
								getListPath: '/accounts?type=income',
								putPath: '/income-account',
								hasHierarchy: true
							},
							{
								key: 'quickbooks_inventory_asset_account',
								title: 'Inventory Asset Account',
								description: 'The asset account where inventory will be recorded.',
								getListPath: '/accounts?type=inventory-asset',
								putPath: '/inventory-asset-account',
								hasHierarchy: true
							},
							{
								key: 'quickbooks_inventory_expense_account',
								title: 'Inventory Expense Account',
								description: 'The expense account where inventory will be recorded.',
								getListPath: '/accounts?type=inventory-expense',
								putPath: '/inventory-expense-account',
								hasHierarchy: true
							},
							{
								key: 'quickbooks_expense_account',
								title: 'Expense Account',
								description: 'The account where agent commissions will be recorded.',
								getListPath: '/accounts?type=expense',
								putPath: '/expense-account',
								hasHierarchy: true
							},
							{
								key: 'quickbooks_paynow_bank_account',
								title: 'Paynow Bank Account',
								type: "Bank Account",
								description: 'The bank account that will be used to record Paynow payments.',
								getListPath: '/bank-accounts',
								putPath: '/paynow-bank-account',
								hasHierarchy: false
							},
							{
								key: 'quickbooks_bad_debts_account',
								title: 'Bad Debts Account',
								type: "Expense Account",
								description: 'The account that will be used to record bad debts',
								getListPath: '/accounts?type=bad-debts',
								putPath: '/bad-debts-account',
								hasHierarchy: false
							}
						].map(({ key, ...rest }) => {
							return <div className="border-b border-b-[1px] last:border-b-0" key={key}>
								<QuickbooksEntity
									value={settings[key]}
									{...rest}
									onChange={value => {
										const settings = { ...this.state.settings, [key]: value }
										this.setState({ settings });
									}}
									disabled={!allowEditingQuickbooksSettings}
									key={key}
								/>
							</div>
						})
					}
				</div>

				<div className="flex flex-col gap-4">

					<div className="text-lg font-bold">Other settings</div>
					<Divider />

					{
						[ 'sales_agent_commission', 'technical_agent_commission', 'late_payment_penalty' ].map((settingName, index) => {
							const value = settings[settingName];
							const label = capitalize(settingName.replace(/_/g, ' ')) + ' (%)';
							
							return <Setting
								key={index}
								settingName={settingName}
								label={label}	
								value={value}
								onChange={value => {
									const settings = { ...this.state.settings, [settingName]: value };
									this.setState({ settings });
								}}
								disabled={!allowEditingSystemSettings}
							/>
						})
					}

					{
						allowUploadingExcel && <>
							<Divider />

							<div>
								<div className="text-lg font-bold">Import from Excel</div>
								<div className="grid grid-cols-[1fr,auto] gap-2">
									<AttachmentInput
										name={this.state.excelFile?.name}
										onChange={excelFile => this.setState({ excelFile })}
										accept=".xls,.xlsx"
									/>
									<div className="flex items-center">
										<IconButton className="rounded-full text-white bg-orange-600" onClick={this.importExcel}>
											<Upload />
										</IconButton>
									</div>
								</div>
							</div>
						</>
					}

				</div>
		
			</div>
		}

      return <Dialog open fullWidth maxWidth="md">
         <DialogTitleWithCloseButton onClose={this.props.onClose}>
            System Settings
         </DialogTitleWithCloseButton>
         <DialogContent dividers>
				{content}
         </DialogContent>
			<DialogActions>
				<RoundedIconButton color="secondary" onClick={this.fetchSettings}>
					<RefreshIcon />
				</RoundedIconButton>
			</DialogActions>
      </Dialog>
   }
}

const mapStateToProps = state => ({ roles: state.user?.roles || [] });
const SystemSettings = connect(mapStateToProps)(_SystemSettings);

export default SystemSettings;